import { formatUnits } from "viem";
import { formatMoney } from "./format-money";

export interface DisplayTokensOptions {
  formattedPrice?: number; // The price used to calculate the equivalent dollar value.
  displayInDollars?: boolean; // Flag to determine if dollar value should be displayed.
  hideTokenAmount?: boolean; // Flag to determine if dollar value should be displayed.
  numberOfDecimals?: number; // The number of decimal places for the token amount.
  maxDecimals?: number; // The number of max decimal places for the token amount.
  decimals?: number; // The number of decimal places to format units.
  fixedNumberOfDecimals?: number; // number.
}

/**
 * Formats the token amount to a specified number of decimal places, appending
 * a label and its equivalent in dollars if required.
 *
 * The function ensures that if `maxDecimals` is not specified, it will display
 * up to the first non-zero digit in the decimal places if the initial decimals are zero.
 *
 * @param tokens - The token amount as a bigint.
 * @param options - Configuration for formatting the display of the token amount.
 * @returns A string formatted according to the specified options.
 *
 * @example
 * ```typescript
 * const tokensDisplay = displayTokens(BigInt('1234500000000000000'), {
 *   formattedPrice: 2000,
 *   displayInDollars: true,
 *   numberOfDecimals: 3,
 *   tokenDecimals: 18,
 * });
 * // Returns: "1.234 (2468.000)"
 * ```
 */
export function displayTokens(
  tokens: bigint | undefined,
  {
    formattedPrice,
    displayInDollars = false,
    hideTokenAmount = false,
    numberOfDecimals = 2,
    maxDecimals,
    decimals = 18,
    fixedNumberOfDecimals,
  }: DisplayTokensOptions
): string {
  let tokenAmount = parseFloat(formatUnits(tokens || BigInt(0), decimals));

  let formattedTokens = "";
  if (!hideTokenAmount) {
    formattedTokens = formatMoney(tokenAmount, {
      numberOfDecimals,
      maxDecimals,
      fixedNumberOfDecimals,
    });
  }

  let formattedDollars = "";
  if (formattedPrice && displayInDollars) {
    const dollarAmount = tokenAmount * formattedPrice;
    formattedDollars = formatMoney(dollarAmount, {
      numberOfDecimals,
      maxDecimals,
      fixedNumberOfDecimals,
    });
  }

  // Choose what to return based on the flags
  if (!hideTokenAmount && displayInDollars) {
    if (formattedDollars) {
      return `${formattedTokens} (${formattedDollars})`;
    }
    return `${formattedTokens}`;
  } else if (!hideTokenAmount) {
    return formattedTokens;
  } else if (displayInDollars) {
    return formattedDollars;
  } else {
    return "";
  }
}
